// Calendario de feriados del hotel (script 925). En esas noches el alojamiento
// tributa el IVA diferenciado (8 %) en lugar del general (15 %): la cotización,
// el cierre nocturno y los cargos lo leen noche por noche. El calendario es el
// mismo de Nómina (nomina.feriados, uno por empresa). Escritura: nivel 5.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarHeart, ChevronLeft, ChevronRight, Download, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useTabsStore } from '@/stores/tabs.store';
import { Campo, Modal, fieldCls, hotelRpc, QK, fmtFecha, useSort, SortTh } from './shared';

interface Feriado { id: string; fecha: string; nombre: string; ambito: 'nacional' | 'local'; observacion: string | null }
interface FeriadosResp { anio: number; iva_feriado_activo: boolean; iva_feriado_pct: number; iva_pct: number; dias: Feriado[] }
type Edicion = { id?: string; fecha: string; nombre: string; ambito: 'nacional' | 'local'; observacion: string };

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const getVal = (f: Feriado, k: string) => (f as unknown as Record<string, string | null>)[k];
const iso = (a: number, m: number, d: number) => `${a}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export function FeriadosHotel() {
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const puedeEditar = isAdmin();
  const openTab = useTabsStore((s) => s.openTab);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [ed, setEd] = useState<Edicion | null>(null);

  const { data, isLoading } = useQuery<FeriadosResp>({
    queryKey: [QK, 'feriados', anio], queryFn: () => hotelRpc<FeriadosResp>('feriados', { _anio: anio }),
  });
  const dias = useMemo(() => data?.dias ?? [], [data]);
  const porFecha = useMemo(() => new Map(dias.map((f) => [f.fecha.slice(0, 10), f])), [dias]);
  const sort = useSort(dias, getVal, 'fecha');
  const sorted = sort.sorted;

  const invalidar = () => void qc.invalidateQueries({ queryKey: [QK] });
  const guardar = useMutation({
    mutationFn: (e: Edicion) => hotelRpc('feriado_guardar', { _d: e }),
    onSuccess: () => { toast.success('Feriado guardado'); setEd(null); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => hotelRpc('feriado_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Feriado eliminado'); setEd(null); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const copiar = useMutation({
    mutationFn: () => hotelRpc<{ insertados: number }>('feriados_copiar_nacionales', { _anio: anio }),
    onSuccess: (r) => { toast.success(r.insertados ? `${r.insertados} feriados nacionales cargados` : 'Los feriados nacionales ya estaban cargados'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirDia = (fecha: string) => {
    if (!puedeEditar) return;
    const f = porFecha.get(fecha);
    setEd(f ? { id: f.id, fecha, nombre: f.nombre, ambito: f.ambito, observacion: f.observacion ?? '' }
            : { fecha, nombre: '', ambito: 'local', observacion: '' });
  };
  const hoy = new Date(); const hoyIso = iso(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><CalendarHeart className="w-5 h-5" /> Feriados (IVA {data?.iva_feriado_pct ?? 8} %)</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAnio(anio - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="w-14 text-center font-semibold">{anio}</span>
          <Button variant="outline" size="sm" onClick={() => setAnio(anio + 1)}><ChevronRight className="w-4 h-4" /></Button>
          {puedeEditar && <>
            <Button variant="outline" size="sm" isLoading={copiar.isPending} onClick={() => copiar.mutate()}><Download className="w-4 h-4 mr-1" /> Cargar nacionales {anio}</Button>
            <Button size="sm" onClick={() => setEd({ fecha: iso(anio, hoy.getMonth(), hoy.getDate()), nombre: '', ambito: 'local', observacion: '' })}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
          </>}
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Las noches que caen en un feriado se cotizan y facturan con IVA del {data?.iva_feriado_pct ?? 8} % en lugar del {data?.iva_pct ?? 15} %.
        Haz clic en un día para marcarlo o editarlo. Este calendario es el mismo que usa Nómina.
      </p>
      {data && !data.iva_feriado_activo && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4" /> El IVA reducido en feriados está <b>desactivado</b>: estos días no cambian el impuesto.
          <button className="underline" onClick={() => openTab({ id: '/hotel/config', title: 'Configuración', path: '/hotel/config' })}>Activarlo en Configuración</button>
        </div>
      )}
      {!puedeEditar && <p className="text-xs text-amber-700 dark:text-amber-400">Solo un administrador (nivel de acceso 5) puede modificar el calendario.</p>}

      {isLoading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {MESES.map((mes, m) => {
            const primero = (new Date(anio, m, 1).getDay() + 6) % 7; // lunes = 0
            const n = new Date(anio, m + 1, 0).getDate();
            return (
              <Card key={mes}>
                <CardHeader className="pb-1 pt-3"><CardTitle className="text-sm">{mes}</CardTitle></CardHeader>
                <CardContent className="pb-3">
                  <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
                    {DIAS.map((d) => <div key={d} className="py-0.5 font-medium text-muted-foreground">{d}</div>)}
                    {Array.from({ length: primero }, (_, i) => <div key={`v${i}`} />)}
                    {Array.from({ length: n }, (_, i) => {
                      const f = iso(anio, m, i + 1); const fer = porFecha.get(f);
                      return (
                        <button key={f} type="button" title={fer ? `${fer.nombre} (${fer.ambito})` : undefined} onClick={() => abrirDia(f)}
                          className={`rounded py-1 tabular-nums ${fer ? (fer.ambito === 'nacional' ? 'bg-rose-500 text-white font-semibold' : 'bg-violet-500 text-white font-semibold')
                            : 'hover:bg-muted'} ${f === hoyIso ? 'ring-1 ring-primary' : ''} ${puedeEditar ? '' : 'cursor-default'}`}>
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-500" /> Nacional</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-violet-500" /> Local / provincial</span>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Feriados {anio}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2 w-10">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="nombre" sort={sort}>Feriado</SortTh>
              <SortTh col="ambito" sort={sort}>Ámbito</SortTh>
              <th className="py-2 pr-2">Observación</th>
            </tr></thead>
            <tbody>
              {sorted.map((f, i) => (
                <tr key={f.id} className={`border-b ${puedeEditar ? 'cursor-pointer hover:bg-muted/50' : ''}`} onClick={() => abrirDia(f.fecha.slice(0, 10))}>
                  <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{fmtFecha(f.fecha)}</td>
                  <td className="py-1.5 pr-2">{f.nombre}</td>
                  <td className="py-1.5 pr-2 capitalize">{f.ambito}</td>
                  <td className="py-1.5 pr-2 text-muted-foreground">{f.observacion ?? ''}</td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin feriados en {anio}. Usa «Cargar nacionales {anio}».</td></tr>}
            </tbody>
            {sorted.length > 0 && <tfoot><tr className="font-semibold"><td colSpan={5} className="py-2">Total: {sorted.length} días feriados</td></tr></tfoot>}
          </table>
        </CardContent>
      </Card>

      {ed && (
        <Modal title={ed.id ? 'Editar feriado' : 'Nuevo feriado'} onClose={() => setEd(null)}>
          <div className="space-y-3 p-4">
            <Campo label="Fecha"><input type="date" className={fieldCls} value={ed.fecha} onChange={(e) => setEd({ ...ed, fecha: e.target.value })} /></Campo>
            <Campo label="Nombre"><input className={fieldCls} maxLength={120} value={ed.nombre} placeholder="Ej.: Fundación de Puerto Ayora" onChange={(e) => setEd({ ...ed, nombre: e.target.value })} /></Campo>
            <Campo label="Ámbito">
              <select className={fieldCls} value={ed.ambito} onChange={(e) => setEd({ ...ed, ambito: e.target.value as Edicion['ambito'] })}>
                <option value="nacional">Nacional</option><option value="local">Local / provincial</option>
              </select>
            </Campo>
            <Campo label="Observación"><input className={fieldCls} value={ed.observacion} placeholder="Ej.: Decreto Ejecutivo N.° …" onChange={(e) => setEd({ ...ed, observacion: e.target.value })} /></Campo>
            <div className="flex justify-between gap-2 pt-2">
              {ed.id ? <Button variant="outline" isLoading={eliminar.isPending} onClick={() => eliminar.mutate(ed.id as string)}><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button> : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEd(null)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!ed.fecha || !ed.nombre.trim()} onClick={() => guardar.mutate(ed)}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
